import React, {
  CSSProperties,
  useContext,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Helmet } from 'react-helmet-async';
import { ArrowRightOutlined, UndoOutlined } from '@ant-design/icons';
import {
  Button,
  Card,
  Empty,
  Input,
  message,
  Result,
  Select,
  Skeleton,
  Space,
  Switch,
  Table,
  Tooltip,
  Typography,
} from 'antd';
import { ColumnProps } from 'antd/es/table';

import DataChangeRequestStatus, {
  DataChangeRequestValue,
} from '../components/dataChangeRequests/DataChangeRequestStatus';
import EditableDataChangeRequestLogo from '../components/dataChangeRequests/EditableDataChangeRequestLogo';
import LanguageSelect from '../components/utils/LanguageSelect';
import { UserContext } from '../contexts/UserContext';
import {
  GetClientPracticesQuery,
  useCreateClientDataChangeRequestMutation,
  useDeleteClientDataChangeRequestMutation,
  useGetClientPracticesQuery,
  useUpdateClientDataChangeRequestMutation,
} from '../graphql/generated';
import {
  draftToPracticeProposedChanges,
  getLivePracticeValues,
  getPracticeBaseline,
  originalValuesForPracticeChanges,
  practiceDraftsEqual,
  PracticeProposedChanges,
  practiceProposedChangesEqual,
  PracticeRowDraft,
  requestToPracticeProposedChanges,
} from './clientPracticeChanges';

type OrganizationNetwork = NonNullable<
  NonNullable<
    NonNullable<GetClientPracticesQuery['users_by_pk']>['client_organization']
  >['organization_networks']
>[0];
type ClientNetwork = OrganizationNetwork['network'];
type ClientPractice = ClientNetwork['practices'][0];
type ChangeRequest = ClientPractice['change_requests'][0];
type PracticeRow = ClientPractice & { network: ClientNetwork };

const pageStyle: CSSProperties = {
  margin: '0 auto',
  maxWidth: 1440,
};

const contentCardStyle: CSSProperties = {
  border: '1px solid #e5e7eb',
  borderRadius: 8,
  boxShadow: '0 8px 24px rgba(15, 23, 42, 0.06)',
};

const editableCellStyle: CSSProperties = {
  alignItems: 'center',
  display: 'flex',
};

const inlineEditInputStyle: CSSProperties = {
  background: 'transparent',
  marginLeft: -6,
  minWidth: 0,
  paddingLeft: 6,
  paddingRight: 6,
};

const pendingControlStyle: CSSProperties = {
  borderRadius: 4,
  outline: '2px solid #d4b106',
  outlineOffset: 1,
};

const withPendingBorder = (
  style: CSSProperties,
  isPending: boolean
): CSSProperties => (isPending ? { ...style, ...pendingControlStyle } : style);

const pendingDeactivationRowStyle: CSSProperties = {
  background: '#e8e8e8',
};

const revertButtonStyle: CSSProperties = {
  marginLeft: 4,
};

const getPendingRequest = (practice: ClientPractice) =>
  practice.change_requests[0];

const isUnderReview = (practice: ClientPractice) =>
  getPendingRequest(practice)?.status === 'reviewing';

const proposes = (
  request: ChangeRequest | undefined,
  field: keyof PracticeProposedChanges
) => field in requestToPracticeProposedChanges(request);

const networkLabel = (network: Pick<ClientNetwork, 'network_name' | 'dc_id'>) =>
  network.dc_id
    ? `${network.network_name} (${network.dc_id})`
    : network.network_name;

const normalizeLanguageCode = (language?: string | null) =>
  language?.replace('/', '-') || undefined;

const RevertProposedFieldButton: React.FC<{
  current: string;
  onRevert: () => void;
}> = ({ current, onRevert }) => (
  <Tooltip title={`Revert to current value: ${current}`}>
    <Button
      aria-label="Revert pending field"
      icon={<UndoOutlined />}
      onClick={onRevert}
      size="small"
      style={revertButtonStyle}
      type="text"
    />
  </Tooltip>
);

const ClientPracticesPage: React.FC = () => {
  const { user } = useContext(UserContext);
  const [drafts, setDrafts] = useState<Record<string, PracticeRowDraft>>({});
  const [savingIds, setSavingIds] = useState<Record<string, boolean>>({});
  const [saveErrors, setSaveErrors] = useState<Record<string, string>>({});
  const { data, loading, error } = useGetClientPracticesQuery({
    variables: { user_id: user?.user_id || '' },
    skip: !user?.user_id,
    notifyOnNetworkStatusChange: true,
  });
  const [createRequest] = useCreateClientDataChangeRequestMutation();
  const [updateRequest] = useUpdateClientDataChangeRequestMutation();
  const [deleteRequest] = useDeleteClientDataChangeRequestMutation();

  const organization = data?.users_by_pk?.client_organization;
  const organizationId = data?.users_by_pk?.client_organization_id;

  const networks = useMemo(() => {
    return (
      organization?.organization_networks
        .map(({ network }) => network)
        .filter((network) => !network.is_archived)
        .sort((a, b) => networkLabel(a).localeCompare(networkLabel(b))) || []
    );
  }, [organization]);

  const practices = useMemo(() => {
    const byId = new Map<string, PracticeRow>();
    networks.forEach((network) => {
      network.practices.forEach((practice) => {
        byId.set(practice.practice_id, { ...practice, network });
      });
    });
    return Array.from(byId.values()).sort((a, b) =>
      a.practice_name.localeCompare(b.practice_name)
    );
  }, [networks]);

  const networkOptions = useMemo(
    () =>
      networks.map((network) => ({
        label: networkLabel(network),
        value: network.network_id,
      })),
    [networks]
  );
  const networkNames = useMemo(
    () =>
      new Map(
        networks.map((network) => [network.network_id, network.network_name])
      ),
    [networks]
  );

  const draftsRef = useRef(drafts);
  draftsRef.current = drafts;
  const practicesRef = useRef(practices);
  practicesRef.current = practices;
  const inFlightRef = useRef<Record<string, boolean>>({});

  const getDraft = (practice: PracticeRow) =>
    drafts[practice.practice_id] || getPracticeBaseline(practice);

  const clearDraft = (practiceId: string) => {
    setDrafts((current) => {
      const next = { ...current };
      delete next[practiceId];
      return next;
    });
  };

  const setDraftFields = (
    practice: PracticeRow,
    changes: Partial<PracticeRowDraft>
  ) => {
    const next = { ...getDraft(practice), ...changes };
    setDrafts((current) => ({
      ...current,
      [practice.practice_id]: next,
    }));
    return next;
  };

  const commitRow = (
    practice: PracticeRow,
    draftOverride?: PracticeRowDraft
  ) => {
    const practiceId = practice.practice_id;
    const draft = draftOverride || draftsRef.current[practiceId];
    if (
      !draft ||
      inFlightRef.current[practiceId] ||
      !organizationId ||
      isUnderReview(practice)
    )
      return;

    if (!draft.practice_name.trim()) {
      message.error("Practice name can't be empty.");
      setDraftFields(practice, {
        practice_name: getPracticeBaseline(practice).practice_name,
      });
      return;
    }

    const proposed = draftToPracticeProposedChanges(practice, draft);
    const pending = getPendingRequest(practice);
    if (
      practiceProposedChangesEqual(
        proposed,
        requestToPracticeProposedChanges(pending)
      )
    ) {
      if (practiceDraftsEqual(draft, getPracticeBaseline(practice))) {
        clearDraft(practiceId);
      }
      return;
    }

    const hasChanges = Object.keys(proposed).length > 0;
    let mutation: Promise<unknown>;

    if (pending && !hasChanges) {
      mutation = deleteRequest({
        variables: {
          data_change_request_id: pending.data_change_request_id,
        },
        refetchQueries: ['getClientPractices'],
        awaitRefetchQueries: true,
      });
    } else if (pending) {
      mutation = updateRequest({
        variables: {
          data_change_request_id: pending.data_change_request_id,
          original_values: originalValuesForPracticeChanges(practice, proposed),
          proposed_changes: proposed,
        },
        refetchQueries: ['getClientPractices'],
        awaitRefetchQueries: true,
      });
    } else {
      mutation = createRequest({
        variables: {
          request: {
            change_type: 'update',
            client_organization_id: organizationId,
            original_values: originalValuesForPracticeChanges(
              practice,
              proposed
            ),
            proposed_changes: proposed,
            target_key: { practice_id: practiceId },
            target_table: 'practices',
          },
        },
        refetchQueries: ['getClientPractices'],
        awaitRefetchQueries: true,
      });
    }

    inFlightRef.current[practiceId] = true;
    setSavingIds((current) => ({ ...current, [practiceId]: true }));
    setSaveErrors((current) => {
      const next = { ...current };
      delete next[practiceId];
      return next;
    });

    mutation
      .then(() => {
        inFlightRef.current[practiceId] = false;
        setSavingIds((current) => ({ ...current, [practiceId]: false }));

        const freshPractice = practicesRef.current.find(
          (candidate) => candidate.practice_id === practiceId
        );
        const latestDraft = draftsRef.current[practiceId];
        if (!freshPractice || !latestDraft) return;
        if (
          practiceDraftsEqual(latestDraft, getPracticeBaseline(freshPractice))
        ) {
          clearDraft(practiceId);
        } else {
          commitRow(freshPractice, latestDraft);
        }
      })
      .catch((saveError: { message?: string }) => {
        inFlightRef.current[practiceId] = false;
        setSavingIds((current) => ({ ...current, [practiceId]: false }));
        setSaveErrors((current) => ({
          ...current,
          [practiceId]: saveError.message || 'Save failed',
        }));
      });
  };

  const revertField = (
    practice: PracticeRow,
    field: keyof PracticeRowDraft
  ) => {
    const live = getLivePracticeValues(practice);
    const next = setDraftFields(practice, { [field]: live[field] });
    commitRow(practice, next);
  };

  const withdrawRequest = (practice: PracticeRow) => {
    const next = setDraftFields(practice, getLivePracticeValues(practice));
    commitRow(practice, next);
  };

  const getNetworkName = (networkId?: string | null) =>
    (networkId && networkNames.get(networkId)) || 'None';

  const pendingChangeLines = (practice: PracticeRow) => {
    const proposed = requestToPracticeProposedChanges(
      getPendingRequest(practice)
    );
    const lines: Array<{
      label: string;
      from: string | null;
      to: string | null;
    }> = [];

    if ('practice_name' in proposed)
      lines.push({
        label: 'Name',
        from: practice.practice_name,
        to: proposed.practice_name || null,
      });
    if ('practice_address' in proposed)
      lines.push({
        label: 'Address',
        from: practice.practice_address || null,
        to: proposed.practice_address || null,
      });
    if ('practice_language' in proposed)
      lines.push({
        label: 'Language',
        from: practice.practice_language || 'EN (default)',
        to: proposed.practice_language || 'EN (default)',
      });
    if ('practice_logo' in proposed)
      lines.push({
        label: 'Logo',
        from: practice.practice_logo ? 'Current logo' : null,
        to: proposed.practice_logo ? 'New logo' : null,
      });
    if ('primary_network_id' in proposed)
      lines.push({
        label: 'Network',
        from: getNetworkName(practice.primary_network_id),
        to: getNetworkName(proposed.primary_network_id),
      });
    if ('is_archived' in proposed)
      lines.push({
        label: 'Active',
        from: practice.is_archived ? 'No' : 'Yes',
        to: proposed.is_archived ? 'No' : 'Yes',
      });

    return lines;
  };

  const isProposedDeactivation = (practice: PracticeRow) =>
    !practice.is_archived && getDraft(practice).is_archived;

  const columns: ColumnProps<PracticeRow>[] = [
    {
      title: 'TIN / CCN',
      key: 'identifiers',
      width: 95,
      render: (_, practice) => (
        <Space direction="vertical" size={0}>
          {practice.tin ? (
            <Typography.Text>{practice.tin}</Typography.Text>
          ) : null}
          {practice.ccn ? (
            <Typography.Text type="secondary">
              CCN {practice.ccn}
            </Typography.Text>
          ) : null}
        </Space>
      ),
    },
    {
      title: 'Practice / Legal Name',
      key: 'practice_name',
      width: 185,
      render: (_, practice) => {
        const pending = getPendingRequest(practice);
        const underReview = isUnderReview(practice);
        const hasPendingChange = proposes(pending, 'practice_name');
        return (
          <div>
            <div style={editableCellStyle}>
              <Input.TextArea
                className="editable-field"
                autoSize={{ minRows: 1 }}
                bordered={false}
                disabled={underReview}
                maxLength={40}
                onBlur={() => commitRow(practice)}
                onChange={(event) =>
                  setDraftFields(practice, {
                    practice_name: event.target.value.replace(/\n/g, ''),
                  })
                }
                onPressEnter={(event) => {
                  event.preventDefault();
                  (event.target as HTMLTextAreaElement).blur();
                }}
                style={withPendingBorder(
                  inlineEditInputStyle,
                  hasPendingChange
                )}
                value={getDraft(practice).practice_name}
              />
              {hasPendingChange && !underReview ? (
                <RevertProposedFieldButton
                  current={practice.practice_name}
                  onRevert={() => revertField(practice, 'practice_name')}
                />
              ) : null}
            </div>
            {practice.legal_business_name ? (
              <Tooltip title="Legal business name; contact support@getmabel.com to change">
                <Typography.Text
                  style={{ display: 'block', fontSize: 12 }}
                  type="secondary"
                >
                  {practice.legal_business_name}
                </Typography.Text>
              </Tooltip>
            ) : null}
          </div>
        );
      },
    },
    {
      title: 'Logo',
      key: 'practice_logo',
      width: 115,
      render: (_, practice) => {
        const pending = getPendingRequest(practice);
        const underReview = isUnderReview(practice);
        const hasPendingChange = proposes(pending, 'practice_logo');
        const logoUrl = getDraft(practice).practice_logo;
        return (
          <EditableDataChangeRequestLogo
            currentLogoUrl={practice.practice_logo}
            disabled={underReview}
            label={practice.practice_name}
            logoUrl={logoUrl}
            onChange={(nextLogoUrl) => {
              const next = setDraftFields(practice, {
                practice_logo: nextLogoUrl,
              });
              commitRow(practice, next);
            }}
            onRevert={() => revertField(practice, 'practice_logo')}
            pending={hasPendingChange}
            uploadPath={`practice/${practice.practice_id}`}
          />
        );
      },
    },
    {
      title: 'Address',
      key: 'practice_address',
      width: 180,
      render: (_, practice) => {
        const pending = getPendingRequest(practice);
        const underReview = isUnderReview(practice);
        const hasPendingChange = proposes(pending, 'practice_address');
        return (
          <div style={editableCellStyle}>
            <Input.TextArea
              className="editable-field"
              autoSize={{ minRows: 1 }}
              bordered={false}
              disabled={underReview}
              onBlur={() => commitRow(practice)}
              onChange={(event) =>
                setDraftFields(practice, {
                  practice_address: event.target.value,
                })
              }
              style={withPendingBorder(inlineEditInputStyle, hasPendingChange)}
              value={getDraft(practice).practice_address || ''}
            />
            {hasPendingChange && !underReview ? (
              <RevertProposedFieldButton
                current={practice.practice_address || 'empty'}
                onRevert={() => revertField(practice, 'practice_address')}
              />
            ) : null}
          </div>
        );
      },
    },
    {
      title: 'Language',
      key: 'practice_language',
      width: 110,
      render: (_, practice) => {
        const pending = getPendingRequest(practice);
        const underReview = isUnderReview(practice);
        const hasPendingChange = proposes(pending, 'practice_language');
        return (
          <div style={editableCellStyle}>
            <LanguageSelect
              disabled={underReview}
              dropdownMatchSelectWidth={260}
              onChange={(value) => {
                const next = setDraftFields(practice, {
                  practice_language: value,
                });
                commitRow(practice, next);
              }}
              optionLabelProp="value"
              placeholder="EN"
              style={withPendingBorder(
                { flex: 1, minWidth: 0 },
                hasPendingChange
              )}
              value={normalizeLanguageCode(
                getDraft(practice).practice_language
              )}
            />
            {hasPendingChange && !underReview ? (
              <RevertProposedFieldButton
                current={practice.practice_language || 'EN (default)'}
                onRevert={() => revertField(practice, 'practice_language')}
              />
            ) : null}
          </div>
        );
      },
    },
    {
      title: 'Network',
      key: 'network',
      width: 165,
      render: (_, practice) => {
        const pending = getPendingRequest(practice);
        const underReview = isUnderReview(practice);
        const hasPendingChange = proposes(pending, 'primary_network_id');
        return (
          <div style={editableCellStyle}>
            <Select
              disabled={underReview}
              onChange={(value) => {
                const next = setDraftFields(practice, {
                  primary_network_id: value,
                });
                commitRow(practice, next);
              }}
              options={networkOptions}
              style={withPendingBorder(
                { flex: 1, minWidth: 0 },
                hasPendingChange
              )}
              value={getDraft(practice).primary_network_id || undefined}
            />
            {hasPendingChange && !underReview ? (
              <RevertProposedFieldButton
                current={getNetworkName(practice.primary_network_id)}
                onRevert={() => revertField(practice, 'primary_network_id')}
              />
            ) : null}
          </div>
        );
      },
    },
    {
      title: 'Active',
      key: 'active',
      width: 65,
      render: (_, practice) => {
        const pending = getPendingRequest(practice);
        const underReview = isUnderReview(practice);
        const hasPendingChange = proposes(pending, 'is_archived');
        return (
          <div style={editableCellStyle}>
            <Switch
              checked={!getDraft(practice).is_archived}
              disabled={underReview}
              onChange={(checked) => {
                const next = setDraftFields(practice, {
                  is_archived: !checked,
                });
                commitRow(practice, next);
              }}
              style={withPendingBorder({}, hasPendingChange)}
            />
            {hasPendingChange && !underReview ? (
              <RevertProposedFieldButton
                current={practice.is_archived ? 'Inactive' : 'Active'}
                onRevert={() => revertField(practice, 'is_archived')}
              />
            ) : null}
          </div>
        );
      },
    },
    {
      title: 'Status',
      key: 'status',
      width: 105,
      render: (_, practice) => {
        const practiceId = practice.practice_id;
        const pending = getPendingRequest(practice);
        const underReview = isUnderReview(practice);
        return (
          <DataChangeRequestStatus
            error={saveErrors[practiceId]}
            pending={Boolean(pending)}
            pendingContent={
              pending ? (
                <Space direction="vertical">
                  <div>
                    {pendingChangeLines(practice).map(({ label, from, to }) => (
                      <div key={label}>
                        <Typography.Text type="secondary">
                          {label}:{' '}
                        </Typography.Text>
                        <DataChangeRequestValue previous value={from} />{' '}
                        <ArrowRightOutlined style={{ fontSize: 10 }} />{' '}
                        <DataChangeRequestValue value={to} />
                      </div>
                    ))}
                  </div>
                  {pending.submission_note ? (
                    <Typography.Text type="secondary">
                      Note: {pending.submission_note}
                    </Typography.Text>
                  ) : null}
                  {!underReview ? (
                    <Button
                      danger
                      onClick={() => withdrawRequest(practice)}
                      size="small"
                    >
                      Withdraw request
                    </Button>
                  ) : null}
                </Space>
              ) : undefined
            }
            retry={() => commitRow(practice)}
            reviewing={underReview}
            saving={Boolean(savingIds[practiceId])}
          />
        );
      },
    },
  ];

  const columnsWithRowState = columns.map((column) => ({
    ...column,
    onCell: (practice: PracticeRow) => ({
      style: isProposedDeactivation(practice)
        ? pendingDeactivationRowStyle
        : undefined,
    }),
  }));

  if (loading && !data) return <Skeleton active />;
  if (error)
    return (
      <Result
        status="error"
        title="There was an error retrieving practices"
        subTitle={error.message}
      />
    );
  if (!organizationId || !organization)
    return (
      <Result
        status="warning"
        title="No client organization found"
        subTitle="Your account must be attached to a client organization before you can request practice changes."
      />
    );

  return (
    <>
      <Helmet>
        <title>Practices | Mabel</title>
      </Helmet>
      <div style={pageStyle}>
        <Typography.Title level={2} style={{ marginBottom: 4 }}>
          Practices
        </Typography.Title>
        <Typography.Paragraph type="secondary" style={{ fontSize: 16 }}>
          Edit practice details inline. Changes save automatically and remain
          editable while awaiting Mabel review.
        </Typography.Paragraph>

        <Card bodyStyle={{ padding: 0 }} style={contentCardStyle}>
          <Table<PracticeRow>
            columns={columnsWithRowState}
            dataSource={practices}
            locale={{
              emptyText: (
                <Empty description="No practices are attached to this organization." />
              ),
            }}
            pagination={{ hideOnSinglePage: true, pageSize: 25 }}
            rowKey="practice_id"
            scroll={{ x: 1020 }}
            size="small"
            tableLayout="fixed"
          />
        </Card>
      </div>
    </>
  );
};

export default ClientPracticesPage;
